import React, { useState, Fragment } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { XMarkIcon, PaperAirplaneIcon } from '@heroicons/react/24/outline';

interface NewConversationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSend: (phoneNumber: string, message: string) => Promise<void>;
}

const MAX_SMS_LENGTH = 160;
const MULTIPART_LENGTH = 153;

export const NewConversationModal: React.FC<NewConversationModalProps> = ({
  isOpen,
  onClose,
  onSend
}) => {
  const [phoneNumber, setPhoneNumber] = useState('');
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const charCount = message.length;
  const smsCount = charCount <= MAX_SMS_LENGTH ? 1 : Math.ceil(charCount / MULTIPART_LENGTH);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!phoneNumber.trim()) {
      setError('Veuillez entrer un numéro de téléphone');
      return;
    }

    if (!message.trim()) {
      setError('Veuillez entrer un message');
      return;
    }

    // Basic phone validation
    const cleanPhone = phoneNumber.replace(/\s+/g, '');
    if (!/^(\+?[0-9]{9,15})$/.test(cleanPhone)) {
      setError('Numéro de téléphone invalide');
      return;
    }

    setSending(true);
    try {
      await onSend(cleanPhone, message.trim());
      setPhoneNumber('');
      setMessage('');
      onClose();
    } catch (err) {
      setError('Erreur lors de l\'envoi du SMS');
    } finally {
      setSending(false);
    }
  };

  const handleClose = () => {
    if (!sending) {
      setPhoneNumber('');
      setMessage('');
      setError(null);
      onClose();
    }
  };

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="sms-modal-overlay" onClose={handleClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="sms-modal-backdrop" />
        </Transition.Child>

        <div className="sms-modal-container">
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0 scale-95"
            enterTo="opacity-100 scale-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100 scale-100"
            leaveTo="opacity-0 scale-95"
          >
            <Dialog.Panel className="sms-modal-panel">
              <div className="sms-modal-header">
                <Dialog.Title as="h3" className="sms-modal-title">
                  Nouveau SMS
                </Dialog.Title>
                <button
                  type="button"
                  className="sms-modal-close"
                  onClick={handleClose}
                  disabled={sending}
                >
                  <XMarkIcon className="sms-modal-close-icon" />
                </button>
              </div>

              <form onSubmit={handleSubmit} className="sms-modal-form">
                {error && (
                  <div className="sms-modal-error">{error}</div>
                )}

                <div className="sms-modal-field">
                  <label htmlFor="phoneNumber" className="sms-modal-label">
                    Numéro de téléphone
                  </label>
                  <input
                    id="phoneNumber"
                    type="tel"
                    className="sms-modal-input"
                    placeholder="+32 471 234 567"
                    value={phoneNumber}
                    onChange={(e) => setPhoneNumber(e.target.value)}
                    disabled={sending}
                  />
                </div>

                <div className="sms-modal-field">
                  <label htmlFor="message" className="sms-modal-label">
                    Message
                  </label>
                  <textarea
                    id="message"
                    className="sms-modal-textarea"
                    placeholder="Tapez votre message..."
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    disabled={sending}
                    rows={4}
                  />
                  <div className="sms-modal-char-count">
                    <span className={charCount > MAX_SMS_LENGTH ? 'sms-char-warning' : ''}>
                      {charCount} caractères
                    </span>
                    {smsCount > 1 && (
                      <span className="sms-part-count">({smsCount} SMS)</span>
                    )}
                  </div>
                </div>

                <div className="sms-modal-actions">
                  <button
                    type="button"
                    className="sms-modal-cancel"
                    onClick={handleClose}
                    disabled={sending}
                  >
                    Annuler
                  </button>
                  <button
                    type="submit"
                    className="sms-modal-submit"
                    disabled={sending || !phoneNumber.trim() || !message.trim()}
                  >
                    {sending ? (
                      'Envoi...'
                    ) : (
                      <>
                        <PaperAirplaneIcon className="sms-modal-submit-icon" />
                        Envoyer
                      </>
                    )}
                  </button>
                </div>
              </form>
            </Dialog.Panel>
          </Transition.Child>
        </div>
      </Dialog>
    </Transition>
  );
};

export default NewConversationModal;
